import { getOrganizationUnitIcon } from '@shared/utils/organization-unit-icon.util';
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { OrganizationUnitService, OrganizationUnitDto } from '@shared/services/organization-unit.service';
import { AssociateMemberDialogComponent } from './associate-member-dialog.component';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PaginatedUsersResponse, UserResponse, UserRole, UserService } from '../../services/user.service';
import { FilterChip, PaginationConfig, TableColumn } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { formatUtcDateTimeForUser } from '@shared/utils/utc-date.util';
import { ToolbarSortSelection } from '@shared/components/table-toolbar/table-toolbar.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { OrganizationUnitItem } from '../../models/filter-data.model';
import { TablePreferencesService } from '@shared/services/table-preferences.service';
import { ADMIN_ACTIVITY_USER_ID } from '../../models/admin-user.constants';

interface UserRoleDisplay {
  label: string;
  unitName?: string | null;
  hasUnit?: boolean;
  unitLevelId?: number | null;
}

@Component({
  selector: 'app-user-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TableSurfaceComponent,
    UnitSelectorComponent,
    RouterModule
  ],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss'
})
export class UserListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    { key: 'username', header: 'Usuario', sortable: true, width: '44%', hideable: true, visible: true },
    { key: 'rolesMore', header: 'Roles', width: '28%', hideable: true, visible: true, sortable: false },
    { key: 'lastActivity', header: 'Última actividad', sortable: true, width: '16%', hideable: true, visible: true },
    { key: 'actions', header: '', width: '12%', minWidth: '160px', hideable: false, visible: true, sortable: false }
  ];
  units: OrganizationUnitDto[] = [];
  roleUnitOptions: OrganizationUnitItem[] = [];
  readonly roleOptions: { value: UserRole; label: string }[] = [
    { value: 'Member', label: 'Miembro' },
    { value: 'Servant', label: 'Siervo' },
    { value: 'UnitManager', label: 'Responsable de unidad' },
    { value: 'MinisterialManager', label: 'Gestión ministerial regional' }
  ];
  currentSort: ToolbarSortSelection = { sortBy: 'username', direction: 'asc' };
  private loadSubscription?: Subscription;
  tableData: UserResponse[] = [];
  isLoading = false;
  errorMessage = '';
  filterOptionsError = '';
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  statusUpdating: Set<string> = new Set();
  private destroy$ = new Subject<void>();

  filterForm = new FormGroup({
    searchTerm: new FormControl('', { nonNullable: true }),
    hasLocalUser: new FormControl<boolean | null>(null),
    role: new FormControl<UserRole | null>(null),
    roleUnitId: new FormControl<string | null>(null),
    isActive: new FormControl<boolean | null>(null)
  });

  constructor(
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private router: Router,
    private readonly organizationUnits: OrganizationUnitService,
    private readonly preferences: TablePreferencesService,
    private readonly dialog: MatDialog
  ) { }

  ngOnInit(): void {
    const savedSort = this.preferences.load('user-list-v2').sort;
    if (savedSort && this.columns.some(column => column.key === savedSort.sortBy && column.sortable)
        && ['asc', 'desc'].includes(savedSort.direction)) this.currentSort = savedSort;
    this.loadUsers();
    this.setupFilterListeners();
    this.loadFilterOptions();
  }

  /**
   * Setup filter listeners for reactive form changes
   */
  private setupFilterListeners(): void {
    this.filterForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadUsers();
      });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.currentPage,
      pageSize: this.pageSize,
      totalCount: this.totalCount,
      totalPages: this.totalPages
    };
  }

  /**
   * Load users from API
   */
  private loadUsers(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.cdr.markForCheck();

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.userService.getAllUsers({
      ...this.filterForm.getRawValue(),
      searchText: this.filterForm.controls.searchTerm.value.trim(),
      sortBy: this.currentSort.sortBy,
      sortDirection: this.currentSort.direction,
      page: this.currentPage,
      pageSize: this.pageSize
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.handleLoadSuccess(response),
        error: () => this.handleLoadError()
      });
  }

  loadFilterOptions(): void {
    this.filterOptionsError = '';
    this.organizationUnits.getAll()
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: units => {
          this.units = [...units].sort((a, b) => a.name.localeCompare(b.name));
          this.roleUnitOptions = this.units.map(unit => ({
            id: unit.id, name: unit.name, levelId: unit.organizationLevelId,
            levelName: unit.levelName ?? '', ancestorNames: unit.ancestorNames
          }));
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'No se pudieron cargar las opciones de filtros. Volvé a intentar.';
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Handle successful user load
   */
  private handleLoadSuccess(response: PaginatedUsersResponse): void {
    try {
      const users = response.items || [];
      this.tableData = users;
      this.currentPage = response.pageNumber || this.currentPage;
      this.pageSize = response.pageSize || this.pageSize;
      this.totalCount = response.totalCount || 0;
      this.totalPages = response.totalPages || Math.ceil(this.totalCount / this.pageSize);
      this.isLoading = false;
      this.errorMessage = '';
    } catch (err) {
      this.handleLoadError();
    }
    this.cdr.markForCheck();
  }

  /**
   * Handle load error
   */
  private handleLoadError(): void {
    this.isLoading = false;
    this.errorMessage = 'Error al cargar los usuarios. Por favor, intente nuevamente.';
    this.tableData = [];
    this.totalCount = 0;
    this.totalPages = 0;
    this.cdr.markForCheck();
  }

  clearFilters(): void {
    this.filterForm.reset({ searchTerm: '' }, { emitEvent: false });
    this.currentPage = 1;
    this.loadUsers();
  }

  get activeFilterChips(): FilterChip[] {
    const value = this.filterForm.getRawValue();
    const unitLabel = (id: string) => this.units.find(unit => unit.id === id)?.name ?? id;
    const labels: Record<string, string | null> = {
      hasLocalUser: value.hasLocalUser === null ? null : `Miembro vinculado: ${value.hasLocalUser ? 'Sí' : 'No'}`,
      role: value.role ? `Rol: ${this.getRoleLabel(value.role)}` : null,
      roleUnitId: value.roleUnitId ? `Unidad de rol: ${unitLabel(value.roleUnitId)}` : null,
      isActive: value.isActive === null ? null : `Activo: ${value.isActive ? 'Sí' : 'No'}`
    };
    return Object.entries(labels).filter(([, label]) => label !== null)
      .map(([key, label]) => ({ key, label: label!, removable: true }));
  }

  onToolbarSearch(searchTerm: string): void {
    this.filterForm.patchValue({ searchTerm });
  }

  onFilterRemove(key: string): void {
    this.filterForm.get(key)?.setValue(null);
  }

  associateMember(user: UserResponse): void {
    this.dialog.open(AssociateMemberDialogComponent, { data: { id: user.id, username: user.username }, width: '560px' })
      .afterClosed().pipe(takeUntil(this.destroy$)).subscribe(saved => {
        if (saved) this.loadUsers();
      });
  }

  onSortChange(sort: ToolbarSortSelection): void {
    this.currentSort = sort;
    this.currentPage = 1;
    this.loadUsers();
  }

  onPageChange(event: { page: number }): void {
    this.currentPage = event.page;
    this.loadUsers();
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.loadUsers();
  }

  createUser(): void {
    this.router.navigate(['/users/new']);
  }

  /**
   * Get role badge class
   */
  getRoleBadgeClass(role: string): string {
    const roleClassMap: { [key: string]: string } = {
      'Administrador': 'table-cell-badge--danger',
      'Responsable de unidad': 'table-cell-badge--warning',
      'Siervo': 'table-cell-badge--success',
      'Gestión ministerial regional': 'table-cell-badge--info',
      'Miembro': 'table-cell-badge--primary'
    };
    return roleClassMap[role] || 'table-cell-badge--neutral';
  }

  getUserRoleLabels(user: UserResponse): string[] {
    return this.getUserRoleDetails(user).map(role => role.label);
  }

  getUserRoleDetails(user: UserResponse): UserRoleDisplay[] {
    if (this.isAdminUser(user)) {
      return [{ label: 'Administrador' }];
    }

    if (!this.hasLocalUser(user)) {
      return [];
    }

    if (user.roleAssignments && user.roleAssignments.length > 0) {
      return user.roleAssignments.map(assignment => ({
        label: this.getRoleLabel(assignment.role),
        unitName: assignment.organizationUnitName,
        hasUnit: !!assignment.organizationUnitId,
        unitLevelId: assignment.organizationUnitLevelId
      }));
    }

    const labels = (user.roles ?? []).map(role => this.getRoleLabel(role));
    return (labels.length > 0 ? labels : ['Miembro']).map(label => ({ label }));
  }

  getVisibleRoleDetails(user: UserResponse): UserRoleDisplay[] {
    return this.getUserRoleDetails(user).slice(0, 2);
  }

  getHiddenRoleCount(user: UserResponse): number {
    return Math.max(0, this.getUserRoleDetails(user).length - 2);
  }

  private getRoleLabel(role: string): string {
    switch (role.toLowerCase()) {
      case 'unitmanager':
        return 'Responsable de unidad';
      case 'servant':
        return 'Siervo';
      case 'ministerialmanager':
        return 'Gestión ministerial regional';
      case 'member':
        return 'Miembro';
      default:
        return role;
    }
  }

  getRoleUnitIcon(role: { unitLevelId?: number | null }): string {
    return getOrganizationUnitIcon(role.unitLevelId);
  }

  /**
   * Actualiza el estado activo/inactivo del usuario
   * Realiza la solicitud al backend y actualiza la tabla
   */
  toggleStatus(user: UserResponse, event: MatSlideToggleChange): void {
    event.source.checked = user.enabled;
    if (this.isStatusUpdating(user.id)) return;
    const enabled = !user.enabled;
    this.statusUpdating.add(user.id);
    this.cdr.markForCheck();
    this.userService.updateUserStatus(user.id, enabled).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.statusUpdating.delete(user.id);
        this.loadUsers();
      },
      error: () => {
        this.statusUpdating.delete(user.id);
        this.errorMessage = 'No se pudo cambiar el estado del usuario. Intentá nuevamente.';
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Verifica si el estado de un usuario esta siendo actualizado
   */
  isStatusUpdating(userId: string): boolean {
    return this.statusUpdating.has(userId);
  }

  hasLocalUser(user: UserResponse): boolean {
    return user.hasLocalUser === true;
  }

  createMember(user: UserResponse): void {
    this.router.navigate(['/people/new'], {
      queryParams: {
        identityUserId: user.id,
        identityEmail: user.email
      }
    });
  }

  viewAdminActivity(): void {
    this.router.navigate(['/users', ADMIN_ACTIVITY_USER_ID, 'activity']);
  }

  /**
   * Check if a user is admin
   */
  isAdminUser(user: UserResponse): boolean {
    return user.username.toLowerCase() === 'admin';
  }

  editUser(user: UserResponse): void {
    this.router.navigate(['/users', user.id]);
  }

  formatDateTime(value?: string | null): string {
    return formatUtcDateTimeForUser(value);
  }

}
